import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Play, RotateCcw, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { mockingService, type MockConversation } from '@/services/mockingService';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface MockChatPanelProps {
  projectId?: string;
  projectName?: string;
  botPersona?: string;
  onPersonaChange?: (persona: string) => void;
}

const DEFAULT_PERSONA = `You are a helpful customer service assistant for an e-commerce platform.
You help users with:
- Order tracking and status
- Product inquiries
- Returns and refunds
- General questions

Be friendly, professional, and concise in your responses.`;

export function MockChatPanel({
  projectId,
  projectName = 'Prototype',
  botPersona: initialPersona,
  onPersonaChange,
}: MockChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [botPersona, setBotPersona] = useState(initialPersona || DEFAULT_PERSONA);
  const [isGeneratingScenario, setIsGeneratingScenario] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const conversationContext = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await mockingService.simulateChatbot({
        projectId: projectId || 'prototype',
        message: userMessage.content,
        botPersona,
        conversationContext,
      });

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      // Fallback mock response when API is not available
      const mockResponse: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `[Mock Response] Thank you for your message: "${userMessage.content}". This is a simulated response from the prototype chatbot. In production, this would connect to the actual AI backend.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, mockResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateScenario = async () => {
    setIsGeneratingScenario(true);
    try {
      const conversations = await mockingService.simulateConversation({
        persona: 'A curious customer looking for product information',
        productDescription: `${projectName} - An AI-powered assistant prototype`,
        numTurns: 4,
        includeDelays: true,
      });

      // Play through the conversation with delays
      for (const conv of conversations) {
        await new Promise((resolve) => setTimeout(resolve, conv.delay_ms || 500));
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: conv.role,
            content: conv.content,
            timestamp: new Date(),
          },
        ]);
      }
    } catch {
      // Fallback mock scenario
      const mockScenario: MockConversation[] = [
        { role: 'user', content: "Hi! I'm interested in learning about your service." },
        {
          role: 'assistant',
          content:
            "Hello! Welcome to our platform. I'd be happy to help you learn more. What specific aspect are you interested in?",
        },
        { role: 'user', content: 'How does the AI assistant work?' },
        {
          role: 'assistant',
          content:
            'Our AI assistant uses advanced language models to understand your questions and provide helpful responses. It can help with customer inquiries, product information, and more. Would you like a demo?',
        },
      ];

      for (const conv of mockScenario) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: conv.role,
            content: conv.content,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsGeneratingScenario(false);
    }
  };

  const handleReset = () => {
    setMessages([]);
  };

  const handlePersonaSave = (newPersona: string) => {
    setBotPersona(newPersona);
    onPersonaChange?.(newPersona);
  };

  return (
    <div className="flex h-full flex-col border-l bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <span className="font-medium">Mock Chat</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleGenerateScenario}
            disabled={isGeneratingScenario}
            title="Generate test scenario"
          >
            {isGeneratingScenario ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleReset}
            title="Reset conversation"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" title="Bot settings">
                <Settings className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Bot Persona Settings</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Bot Persona / System Prompt</Label>
                  <Textarea
                    value={botPersona}
                    onChange={(e) => setBotPersona(e.target.value)}
                    rows={10}
                    placeholder="Define the chatbot's personality and behavior..."
                  />
                  <p className="text-xs text-muted-foreground">
                    This defines how the mock chatbot will respond during prototype testing.
                  </p>
                </div>
                <Button onClick={() => handlePersonaSave(botPersona)} className="w-full">
                  Save Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBotPersona(DEFAULT_PERSONA)}
                  className="w-full"
                >
                  Reset to Default
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <Bot className="h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 font-medium">Mock Chat Simulation</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Test your prototype's chat interface with simulated responses.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateScenario}
                disabled={isGeneratingScenario}
              >
                {isGeneratingScenario ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Generate Scenario
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3',
                  message.role === 'user' ? 'flex-row-reverse' : ''
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  {message.role === 'user' ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
                <div
                  className={cn(
                    'max-w-[80%] rounded-lg px-3 py-2',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <span className="mt-1 block text-xs opacity-70">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a test message..."
            disabled={isLoading}
          />
          <Button type="submit" size="icon" disabled={!input.trim() || isLoading}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
